import type { MeetingRecording } from '@/lib/api/meeting-recordings.api'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CircleNotchIcon, WarningCircleIcon } from '@phosphor-icons/react'

const STATUS_CONFIG: Record<
  string,
  { label: string; className: string; animated?: boolean }
> = {
  UPLOADING: {
    label: 'Uploading…',
    className:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300',
    animated: true,
  },
  UPLOADED: {
    label: 'Queued',
    className:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300',
    animated: true,
  },
  TRANSCRIBING: {
    label: 'Transcribing…',
    className:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
    animated: true,
  },
  SUMMARIZING: {
    label: 'Summarizing…',
    className:
      'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300',
    animated: true,
  },
  READY: {
    label: 'Ready',
    className:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  },
  FAILED: {
    label: 'Failed',
    className:
      'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  },
}

export function formatRecordingDuration(seconds?: number | null): string {
  if (!seconds) return '—'
  // Round once on total minutes so 59.6 min reads "1h 0m", not "60m".
  const totalMinutes = Math.round(seconds / 60)
  // A short clip is not an empty one: "0m" read as a failed recording.
  if (totalMinutes === 0) return '<1m'
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/**
 * A failed recording can be reprocessed only when its audio reached the
 * server. An abandoned upload was aborted, so Retry would just fail again.
 */
export function canRetryRecording(recording: {
  status: string
  errorStage?: string | null
}): boolean {
  return recording.status === 'FAILED' && recording.errorStage !== 'UPLOAD'
}

/** Friendly one-line reason for a failed recording (raw error in tooltip). */
export function recordingFailureMessage(errorStage?: string | null): string {
  switch (errorStage) {
    case 'UPLOAD':
      return "The audio never finished uploading, so there's nothing to process."
    case 'TRANSCRIBE':
      return "Couldn't transcribe the audio — try again."
    case 'SUMMARIZE':
      return "Transcribed, but couldn't generate notes — retry to finish."
    default:
      return 'Processing failed — try again.'
  }
}

export function RecordingStatusChip({
  recording,
  className,
}: {
  recording: MeetingRecording
  className?: string
}) {
  const config = STATUS_CONFIG[recording.status] ?? {
    label: recording.status,
    className: 'bg-muted text-muted-foreground',
  }
  const chip = (
    <Badge
      variant="outline"
      className={cn(
        'shrink-0 gap-1.5 font-medium',
        config.className,
        className,
      )}
    >
      {config.animated && <CircleNotchIcon className="h-3 w-3 animate-spin" />}
      {recording.status === 'FAILED' && (
        <WarningCircleIcon className="h-3 w-3" />
      )}
      {config.label}
    </Badge>
  )
  if (recording.status === 'FAILED' && recording.errorMessage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent className="max-w-sm">
          {recording.errorMessage}
        </TooltipContent>
      </Tooltip>
    )
  }
  return chip
}
